import { DateTime, Duration, Interval } from 'luxon';

import { getApiErrorCode } from '../lib/error.js';
import { DetectionStatus } from '../types/detection-status.js';
import { Suppression } from '../types/suppression.js';
import { SuppressionStatus } from '../types/suppression-status.js';

export const STOPPABLE_STATUSES = [
  DetectionStatus.SUPPRESSION_INITIATED,
  DetectionStatus.SUPPRESSION_IN_PROGRESS,
];

// `whitelisted` is excluded deliberately: re-suppressing a group an operator whitelisted is a
// footgun. The stoppable statuses above keep Stop as their action instead.
export const REPEATABLE_STATUSES = [
  DetectionStatus.DETECTED,
  DetectionStatus.SUPPRESSION_SUCCESS,
  DetectionStatus.SUPPRESSION_FAILED,
  DetectionStatus.LOST,
];

// Deliberately not `REPEATABLE_STATUSES`: `detected` on a synthetic group is a data anomaly (a
// synthetic group is born suppressing, never detected), `lost` is out of scope for the multi
// repeat, and `whitelisted` is excluded for the same footgun reason as above.
export const MULTI_FREQUENCY_REPEATABLE_STATUSES = [
  DetectionStatus.SUPPRESSION_SUCCESS,
  DetectionStatus.SUPPRESSION_FAILED,
];

// Classify on `code`, never HTTP status — the same 400/404 on this route also carry
// device-offline / device-not-found rejections.
export const BENIGN_STOP_CODES = ['SUPPRESSION_NOT_FOUND', 'SUPPRESSION_ALREADY_FINISHED'] as const;
export type BenignStopCode = (typeof BENIGN_STOP_CODES)[number];

const isBenignCode = (code: unknown): code is BenignStopCode =>
  typeof code === 'string' && (BENIGN_STOP_CODES as readonly string[]).includes(code);

/** Reads the machine-readable error code off a broadband-suppression stop failure. */
export function getStopErrorCode(error: unknown): string | undefined {
  return getApiErrorCode(error);
}

/** Benign = row already finished or gone; see `BENIGN_STOP_CODES` for why never status-based. */
export function isBenignStopError(error: unknown): boolean {
  return isBenignCode(getStopErrorCode(error));
}

/**
 * Derives the suppression lifecycle status from timestamps and flags.
 */
export const getSuppressionStatus = (suppression: Suppression): SuppressionStatus => {
  if (suppression.finishedAt) {
    if (!suppression.success) {
      return SuppressionStatus.FAILED;
    }

    return suppression.isStopped ? SuppressionStatus.STOPPED : SuppressionStatus.SUCCESS;
  }

  if (suppression.isStopped) {
    return SuppressionStatus.STOPPED;
  }

  if (suppression.startedAt) {
    return SuppressionStatus.IN_PROGRESS;
  }

  return SuppressionStatus.INITIATED;
};

export const suppressionDuration = (suppression: Suppression): Duration | null =>
  suppression.startedAt &&
  Duration.fromMillis(
    Math.trunc(
      Interval.fromDateTimes(
        suppression.startedAt,
        suppression.finishedAt || DateTime.now(),
      ).length('milliseconds') / 1000,
    ) * 1000,
  ).shiftTo('hours', 'minutes', 'seconds');
